<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import DynamicTabs from '@/components/DynamicTabs.vue';

// PROPS
const props = defineProps<{
  activeTab: string;
  isTemplate: boolean;
  disabledTabs?: string[];
}>();

// EMITS
const emit = defineEmits<{
  (e: 'update', tabId: string): void;
}>();

// CONST
const { t } = useI18n();

// REFS
const dynamicTabsRef = ref<InstanceType<typeof DynamicTabs> | null>(null);

// COMPUTED
const tabs = computed(() => {
  return [
    { id: 'create', name: t('consultation.tabs.create') },
    { id: 'engage', name: t('consultation.tabs.engage') },
    { id: 'analyze', name: t('consultation.tabs.analyze') },
  ].map((tab) => ({
    ...tab,
    disable: props.disabledTabs?.includes(tab.id) || tab.id === 'engage' || (tab.id === 'analyze' && props.isTemplate),
  }));
});

// FUNCTIONS
const handleUpdate = (tabId: string) => {
  emit('update', tabId);
};

const setActiveTab = (tabId: string) => {
  if (dynamicTabsRef.value) {
    dynamicTabsRef.value.setActiveTab(tabId);
  }
};

defineExpose({
  setActiveTab,
});
</script>
<template>
  <div class="consultation-navigation-tabs">
    <dynamic-tabs
      ref="dynamicTabsRef"
      :tabs="tabs"
      :vertical="false"
      :show-splitter="false"
      :active="props.activeTab"
      @update="handleUpdate"
    />
  </div>
</template>
<style lang="scss">
.consultation-navigation-tabs {
  height: inherit;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  .dynamic-tabs {
    height: inherit;

    .q-tabs__content {
      gap: $space-xl;
    }

    .q-tab {
      align-content: center;
      padding: 0;
      min-height: 30px;
    }

    .q-tab__content {
      align-content: center;
      padding-bottom: 0;
    }

    .q-tab--active {
      background-color: transparent;

      .q-tab__label {
        display: flex;
      }
    }
  }
}
</style>
